/** An employee's profile page and personal data. */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COUNTRIES } from "@offertli/shared";
import { StatusBadge } from "../../components/StatusBadge.js";
import { Button } from "../../components/ui/Button.js";
import { Select as Dropdown } from "@primereact/ui/select";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { InputNumber } from "@primereact/ui/inputnumber";
import { DateInput } from "../../components/ui/controls.js";
import { ToggleSwitch as InputSwitch } from "@primereact/ui/toggleswitch";
import { PageHeader } from "../../components/ui/PageHeader.js";
import { Tabs } from "@primereact/ui/tabs";
import { Textarea } from "@primereact/ui/textarea";
import { apiGet, apiPatch } from "../../lib/api.js";
import { useMe, usePermissions } from "../../lib/auth.js";
import {
  Detail,
  DetailGrid,
  ErrorPanel,
  FlatSections,
  LoadingPanel,
  MutationError,
  Section,
} from "../shared.js";
import { SalarySettingsPanel } from "./Compensation.js";
import { EmployeeExpensesPanel } from "./Expenses.js";
import { EmployeeWorkDetailsPanel } from "./WorkDetails.js";
import type { EmployeeProfileData } from "./types.js";
import {
  maskIban,
  normalizeEmployee,
  type RawEmployee,
} from "./employee-common.js";
import {
  CalendarFeedPanel,
  LanguagePanel,
  SecurityPanel,
} from "./AccountPanels.js";
import { EmployeeDocuments } from "./EmployeeDocuments.js";
import { ManagementActionsPanel } from "./EmployeeLifecycle.js";
import { EmployeeTimeSettings } from "./EmployeeTimeSettings.js";

export function EmployeeProfilePage() {
  const { t } = useTranslation();
  const params = useParams();
  const me = useMe();
  const permissions = usePermissions();
  const canList = permissions.has("employees.view_all");
  const requestedId = params.id;
  const ownId = me.data?.user.id;
  const userId =
    canList && requestedId && requestedId !== "me" ? requestedId : ownId;
  const [tab, setTab] = useState<
    | "overview"
    | "time"
    | "documents"
    | "salary"
    | "expenses"
    | "calendar"
    | "security"
    | "language"
    | "actions"
  >("overview");
  const profile = useQuery({
    queryKey: ["employee", userId],
    queryFn: () =>
      apiGet<{
        data: RawEmployee;
      }>(`/employees/${userId}`).then((response) =>
        normalizeEmployee(response.data),
      ),
    enabled: Boolean(userId),
  });
  if (me.isLoading || !userId || profile.isLoading) return <LoadingPanel />;
  if (!canList && requestedId && requestedId !== "me") {
    return <Navigate to="/employees/me" replace />;
  }
  if (profile.error || !profile.data)
    return <ErrorPanel error={profile.error} />;
  const employee = profile.data;
  const isOwn = employee.user.id === ownId;
  const canManage = canList && !isOwn && permissions.has("employees.edit");
  const canEditSalary = !isOwn && permissions.has("employees.edit");
  const tabs: Array<[typeof tab, string]> = [
    ["overview", isOwn ? t("employees.myData") : t("employees.overview")],
    ["time", t("employees.timeBalances")],
    ["documents", t("documents.title")],
  ];
  if (isOwn || canEditSalary) {
    tabs.push(["salary", t("employees.compensation")]);
  }
  const canViewExpenses = isOwn
    ? permissions.has("expenses.view_own") || permissions.has("expenses.submit")
    : permissions.has("expenses.view_all") || permissions.has("expenses.pay");
  if (canViewExpenses) {
    tabs.push(["expenses", t("employees.expenses")]);
  }
  if (isOwn) {
    tabs.push(
      ["calendar", t("employees.calendarFeed")],
      ["security", t("employees.security")],
      ["language", t("employees.language")],
    );
  } else if (permissions.has("employees.offboard")) {
    tabs.push(["actions", t("employees.actions")]);
  }
  return (
    <>
      <PageHeader
        title={`${employee.user.firstName} ${employee.user.lastName}`}
        subtitle={
          [employee.profile?.jobTitle, employee.profile?.employeeNumber]
            .filter(Boolean)
            .join(" · ") || employee.user.email
        }
        actions={
          isOwn ? (
            <Link
              className="text-sm font-medium text-brand hover:underline"
              to="/time"
            >
              {t("time.myTime")}
            </Link>
          ) : undefined
        }
      />
      <Tabs.Root
        value={tab}
        onValueChange={(event: { value: unknown }) => {
          const next = String(event.value);
          if (tabs.some(([key]) => key === next)) setTab(next as typeof tab);
        }}
      >
        <Tabs.List>
          {tabs.map(([key, label]) => (
            <Tabs.Tab key={key} value={key}>
              {label}
            </Tabs.Tab>
          ))}
          <Tabs.Indicator />
        </Tabs.List>
        <Tabs.Panels>
          {tabs.map(([key]) => (
            <Tabs.Panel key={key} value={key}>
              <FlatSections>
                {key === "overview" ? (
                  <ProfileOverview
                    data={employee}
                    canManage={canManage}
                    isOwn={isOwn}
                  />
                ) : null}
                {key === "time" ? (
                  <EmployeeTimeSettings data={employee} canManage={canManage} />
                ) : null}
                {key === "documents" ? (
                  <EmployeeDocuments
                    userId={employee.user.id}
                    canManage={
                      permissions.has("employee_documents.manage") && !isOwn
                    }
                  />
                ) : null}
                {key === "salary" ? (
                  <SalarySettingsPanel
                    employee={employee}
                    readOnly={!canEditSalary}
                  />
                ) : null}
                {key === "expenses" ? (
                  <EmployeeExpensesPanel userId={employee.user.id} />
                ) : null}
                {key === "time" &&
                !isOwn &&
                permissions.has("time.view_all") ? (
                  <EmployeeWorkDetailsPanel userId={employee.user.id} />
                ) : null}
                {key === "calendar" && isOwn ? <CalendarFeedPanel /> : null}
                {key === "security" && isOwn ? <SecurityPanel /> : null}
                {key === "language" && isOwn ? <LanguagePanel /> : null}
                {key === "actions" && !isOwn ? (
                  <ManagementActionsPanel data={employee} />
                ) : null}
              </FlatSections>
            </Tabs.Panel>
          ))}
        </Tabs.Panels>
      </Tabs.Root>
    </>
  );
}
/** Fields employees keep up to date themselves (see the API's self-edit rule). */
const PERSONAL_FIELDS = new Set([
  "phone",
  "addressLine1",
  "addressLine2",
  "postalCode",
  "city",
  "country",
  "dateOfBirth",
  "nationality",
  "ahvNumber",
  "bankName",
  "bankIban",
]);

function ProfileOverview({
  data,
  canManage,
  isOwn,
}: {
  data: EmployeeProfileData;
  canManage: boolean;
  isOwn: boolean;
}) {
  const { t, i18n } = useTranslation();
  const profile = data.profile;
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => ({
    employeeNumber: profile?.employeeNumber ?? "",
    phone: profile?.phone ?? "",
    addressLine1: profile?.addressLine1 ?? "",
    addressLine2: profile?.addressLine2 ?? "",
    postalCode: profile?.postalCode ?? "",
    city: profile?.city ?? "",
    country: profile?.country ?? "CH",
    dateOfBirth: profile?.dateOfBirth ?? "",
    ahvNumber: profile?.ahvNumber ?? "",
    bankName: profile?.bankName ?? "",
    bankIban: profile?.bankIban ?? "",
    nationality: profile?.nationality ?? "",
    jobTitle: profile?.jobTitle ?? "",
    employmentStartDate: profile?.employmentStartDate ?? "",
    employmentEndDate: profile?.employmentEndDate ?? "",
    pensumPercent: profile?.pensumPercent ?? "100",
    vacationWeeksOverride: profile?.vacationWeeksOverride ?? "",
    notes: profile?.notes ?? "",
  }));
  const [employmentEndEnabled, setEmploymentEndEnabled] = useState(
    Boolean(profile?.employmentEndDate),
  );
  const countryOptions = useMemo(() => {
    const displayNames = new Intl.DisplayNames(
      [i18n.resolvedLanguage ?? "en"],
      {
        type: "region",
      },
    );
    return COUNTRIES.map((value) => ({
      value,
      label: displayNames.of(value) ?? value,
    }));
  }, [i18n.resolvedLanguage]);
  const save = useMutation({
    mutationFn: () =>
      apiPatch(
        `/employees/${data.user.id}/profile`,
        canManage
          ? {
              ...Object.fromEntries(
                Object.entries(draft)
                  .filter(
                    ([key]) =>
                      ![
                        "employmentStartDate",
                        "employmentEndDate",
                        "notes",
                      ].includes(key),
                  )
                  .map(([key, value]) => [key, value || null]),
              ),
              employmentStart: draft.employmentStartDate || null,
              employmentEnd: employmentEndEnabled
                ? draft.employmentEndDate || null
                : null,
              internalNotes: draft.notes || null,
            }
          : Object.fromEntries(
              Object.entries(draft)
                .filter(([key]) => PERSONAL_FIELDS.has(key))
                .map(([key, value]) => [key, value || null]),
            ),
      ),
    onSuccess: async () => {
      setEditing(false);
      await client.invalidateQueries({
        queryKey: ["employee", data.user.id],
      });
    },
  });
  if (!editing)
    return (
      <Section title={t("employees.employmentProfile")}>
        <DetailGrid>
          <Detail label={t("common.email")} value={data.user.email} />
          <Detail label={t("employees.phone")} value={profile?.phone} />
          <Detail label={t("employees.jobTitle")} value={profile?.jobTitle} />
          <Detail
            label={t("employees.employeeNumber")}
            value={profile?.employeeNumber}
          />
          <Detail
            label={t("employees.pensum")}
            value={`${profile?.pensumPercent ?? "100"}%`}
          />
          <Detail
            label={t("employees.vacationWeeksOverride")}
            value={profile?.vacationWeeksOverride}
          />
          <Detail
            label={t("common.status")}
            value={<StatusBadge status={data.user.status} />}
          />
          <Detail
            label={t("employees.employmentStart")}
            value={profile?.employmentStartDate}
          />
          <Detail
            label={t("employees.employmentEnd")}
            value={profile?.employmentEndDate}
          />
          <Detail label={t("employees.ahvNumber")} value={profile?.ahvNumber} />
          <Detail label={t("employees.bankName")} value={profile?.bankName} />
          <Detail
            label={t("employees.bankIban")}
            value={maskIban(profile?.bankIban)}
          />
          <Detail
            label={t("employees.dateOfBirth")}
            value={profile?.dateOfBirth}
          />
          <Detail
            label={t("employees.nationality")}
            value={profile?.nationality}
          />
          <Detail
            label={t("employees.address")}
            value={[
              profile?.addressLine1,
              profile?.addressLine2,
              [profile?.postalCode, profile?.city].filter(Boolean).join(" "),
              profile?.country,
            ]
              .filter(Boolean)
              .join(", ")}
          />
        </DetailGrid>
        {profile?.notes ? (
          <p className="mt-5 whitespace-pre-wrap text-sm text-app-muted">
            {profile.notes}
          </p>
        ) : null}
        {canManage || isOwn ? (
          <Button
            className="mt-6"
            variant="outlined"
            severity="secondary"
            onClick={() => setEditing(true)}
          >
            {isOwn && !canManage
              ? t("employees.editPersonal")
              : t("common.edit")}
          </Button>
        ) : null}
      </Section>
    );
  const set = (key: keyof typeof draft, value: string) =>
    setDraft((current) => ({
      ...current,
      [key]: value,
    }));
  return (
    <Section title={t("employees.editProfile")}>
      <form
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        {canManage ? (
          <Field label={t("employees.employeeNumber")}>
            <Input
              value={draft.employeeNumber}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                set("employeeNumber", e.target.value)
              }
            />
          </Field>
        ) : null}
        {canManage ? (
          <Field label={t("employees.jobTitle")}>
            <Input
              value={draft.jobTitle}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                set("jobTitle", e.target.value)
              }
            />
          </Field>
        ) : null}
        {canManage ? (
          <Field label={t("employees.pensum")}>
            <InputNumber.Root
              fluid
              min={0.01}
              max={100}
              step={0.01}
              value={
                draft.pensumPercent === "" ? null : Number(draft.pensumPercent)
              }
              onValueChange={(event: { value: number | null }) =>
                set(
                  "pensumPercent",
                  event.value == null ? "" : String(event.value),
                )
              }
            >
              <InputNumber.Input as={Input} />
            </InputNumber.Root>
          </Field>
        ) : null}
        {canManage ? (
          <Field label={t("employees.vacationWeeksOverride")}>
            <InputNumber.Root
              fluid
              min={0}
              max={20}
              minFractionDigits={0}
              maxFractionDigits={2}
              value={
                draft.vacationWeeksOverride === ""
                  ? null
                  : Number(draft.vacationWeeksOverride)
              }
              onValueChange={(event: { value: number | null }) =>
                set(
                  "vacationWeeksOverride",
                  event.value == null ? "" : String(event.value),
                )
              }
            >
              <InputNumber.Input as={Input} />
            </InputNumber.Root>
          </Field>
        ) : null}
        <Field label={t("employees.phone")}>
          <Input
            value={draft.phone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set("phone", e.target.value)
            }
          />
        </Field>
        <Field label={t("employees.dateOfBirth")}>
          <DateInput
            value={draft.dateOfBirth}
            onChange={(value) => set("dateOfBirth", value)}
          />
        </Field>
        <Field label={t("employees.nationality")}>
          <Dropdown.Root
            value={draft.nationality || null}
            options={countryOptions}
            optionLabel="label"
            optionValue="value"
            filter
            onValueChange={(event: { value: unknown }) =>
              set("nationality", (event.value as string) ?? "")
            }
            fluid
          >
            <Dropdown.Trigger>
              <Dropdown.Value />
              <Dropdown.Indicator className="pi pi-chevron-down" />
            </Dropdown.Trigger>
            <Dropdown.Clear className="pi pi-times" />
            <Dropdown.Portal>
              <Dropdown.Positioner>
                <Dropdown.Popup>
                  <Dropdown.Filter />
                  <Dropdown.List />
                </Dropdown.Popup>
              </Dropdown.Positioner>
            </Dropdown.Portal>
          </Dropdown.Root>
        </Field>
        <Field label={t("employees.ahvNumber")}>
          <Input
            placeholder={t("employees.ahvPlaceholder")}
            value={draft.ahvNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set("ahvNumber", e.target.value)
            }
          />
        </Field>
        <Field label={t("employees.bankName")}>
          <Input
            value={draft.bankName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set("bankName", e.target.value)
            }
          />
        </Field>
        <Field label={t("employees.bankIban")}>
          <Input
            type="password"
            autoComplete="off"
            value={draft.bankIban}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set("bankIban", e.target.value)
            }
          />
        </Field>
        {canManage ? (
          <Field label={t("employees.employmentStart")}>
            <DateInput
              value={draft.employmentStartDate}
              onChange={(value) => set("employmentStartDate", value)}
            />
          </Field>
        ) : null}
        {canManage ? (
          <Field label={t("employees.employmentEnd")}>
            <div className="flex items-center gap-3">
              <InputSwitch.Root
                checked={employmentEndEnabled}
                onCheckedChange={(event: { checked: boolean }) => {
                  const enabled = Boolean(event.checked);
                  setEmploymentEndEnabled(enabled);
                  if (!enabled) set("employmentEndDate", "");
                }}
              >
                <InputSwitch.Control>
                  <InputSwitch.Handle></InputSwitch.Handle>
                </InputSwitch.Control>
              </InputSwitch.Root>
              <span className="text-sm text-app-muted">
                {employmentEndEnabled
                  ? t("employees.employmentEndEnabled")
                  : t("employees.noEmploymentEnd")}
              </span>
            </div>
            {employmentEndEnabled ? (
              <DateInput
                value={draft.employmentEndDate}
                onChange={(value) => set("employmentEndDate", value)}
                className="mt-2 w-full"
              />
            ) : null}
          </Field>
        ) : null}
        <Field label={t("employees.addressLine1")}>
          <Input
            value={draft.addressLine1}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set("addressLine1", e.target.value)
            }
          />
        </Field>
        <Field label={t("employees.addressLine2")}>
          <Input
            value={draft.addressLine2}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set("addressLine2", e.target.value)
            }
          />
        </Field>
        <Field label={t("employees.postalCode")}>
          <Input
            value={draft.postalCode}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set("postalCode", e.target.value)
            }
          />
        </Field>
        <Field label={t("employees.city")}>
          <Input
            value={draft.city}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              set("city", e.target.value)
            }
          />
        </Field>
        <Field label={t("employees.country")}>
          <Dropdown.Root
            value={draft.country || null}
            options={countryOptions}
            optionLabel="label"
            optionValue="value"
            filter
            onValueChange={(event: { value: unknown }) =>
              set("country", (event.value as string) ?? "")
            }
            fluid
          >
            <Dropdown.Trigger>
              <Dropdown.Value />
              <Dropdown.Indicator className="pi pi-chevron-down" />
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Positioner>
                <Dropdown.Popup>
                  <Dropdown.Filter />
                  <Dropdown.List />
                </Dropdown.Popup>
              </Dropdown.Positioner>
            </Dropdown.Portal>
          </Dropdown.Root>
        </Field>
        {canManage ? (
          <div className="sm:col-span-2 lg:col-span-3">
            <Field label={t("common.notes")}>
              <Textarea
                rows={3}
                value={draft.notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  set("notes", e.target.value)
                }
              />
            </Field>
          </div>
        ) : null}
        <MutationError error={save.error} />
        <div className="flex gap-2 sm:col-span-2 lg:col-span-3">
          <Button type="submit" loading={save.isPending || undefined}>
            {t("common.save")}
          </Button>
          <Button
            type="button"
            variant="outlined"
            severity="secondary"
            onClick={() => setEditing(false)}
          >
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </Section>
  );
}
